import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useSnack } from '../../services/SnakbarProvider';
import { handleError } from '../../services/errorHandler';
import { useCompany } from '../../contexts/CompanyContext';
import { getInboundList, updateInboundStatus } from '../../api/inbounds/index.ts';
import { getOutboundList, updateOutboundStatus } from '../../api/outbounds/index.ts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Inbound {
  id: number;
  description: string;
  warehouseName: string;
  createdAt: string;
  createdBy: string;
  status: {
    name: string;
  };
}

interface Outbound {
  id: number;
  workOrder: string;
  warehouseOrigin: {
    name: string;
  };
  outboundItems?: Array<{
    product: {
      name: string;
      description?: string;
      code?: string;
    };
    quantity: number;
  }>;
  requestedBy: string;
  createdAt: string;
  status: {
    name: string;
  };
}

const InventoryApprovalScreen = () => {
  const [tabValue, setTabValue] = useState(0);
  const [inbounds, setInbounds] = useState<Inbound[]>([]);
  const [outbounds, setOutbounds] = useState<Outbound[]>([]);
  const [loading, setLoading] = useState(true);
  const { openSnack } = useSnack();
  const { selectedCompany } = useCompany();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchInbounds = async () => {
    try {
      setLoading(true);
      const response = await getInboundList({ companyId: selectedCompany?.id, status: 'pending' });
      setInbounds(response.data);
    } catch (error) {
      openSnack(handleError(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutbounds = async () => {
    try {
      setLoading(true);
      const response = await getOutboundList({ companyId: selectedCompany?.id, status: 'pending' });
      setOutbounds(response.data);
    } catch (error) {
      openSnack(handleError(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInboundAction = async (id: number, action: 'approve' | 'reject') => {
    try {
      await updateInboundStatus(id, action);
      fetchInbounds();
      openSnack(`Entrada ${action === 'approve' ? 'aprobada' : 'rechazada'} exitosamente`, 'success');
    } catch (error) {
      openSnack(handleError(error), 'error');
    }
  };

  const handleOutboundAction = async (id: number, action: 'approve' | 'reject') => {
    try {
      await updateOutboundStatus(id, action);
      fetchOutbounds();
      openSnack(`Salida ${action === 'approve' ? 'aprobada' : 'rechazada'} exitosamente`, 'success');
    } catch (error) {
      openSnack(handleError(error), 'error');
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchInbounds();
      fetchOutbounds();
    }
  }, [selectedCompany]);

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>Seleccione una compañía primero</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  const renderInboundCard = (inbound: Inbound) => (
    <Grid item xs={12} sm={6} md={4} key={inbound.id}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Entrada #{inbound.id}
          </Typography>
          <Typography color="textSecondary">
            Almacén: {inbound.warehouseName}
          </Typography>
          <Typography color="textSecondary">
            Descripción: {inbound.description}
          </Typography>
          <Typography color="textSecondary">
            Creado por: {inbound.createdBy}
          </Typography>
          <Typography color="textSecondary">
            Fecha: {new Date(inbound.createdAt).toLocaleDateString()}
          </Typography>
        </CardContent>
        <CardActions>
          <Button 
            size="small" 
            color="primary" 
            onClick={() => handleInboundAction(inbound.id, 'approve')}
          >
            Aprobar
          </Button>
          <Button 
            size="small" 
            color="error" 
            onClick={() => handleInboundAction(inbound.id, 'reject')}
          >
            Rechazar
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const renderOutboundCard = (outbound: Outbound) => (
    <Grid item xs={12} sm={6} md={4} key={outbound.id}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Salida #{outbound.id}
          </Typography>
          <Typography color="textSecondary">
            Orden de trabajo: {outbound.workOrder}
          </Typography>
          <Typography color="textSecondary">
            Almacén: {outbound.warehouseOrigin.name}
          </Typography>
          {outbound.outboundItems && outbound.outboundItems.length > 0 ? (
            <Box>
              <Typography color="textSecondary" variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                Productos:
              </Typography>
              {outbound.outboundItems.map((item: any, index: number) => (
                <Typography key={index} color="textSecondary" sx={{ pl: 2 }}>
                  • {item.quantity} x {item.product?.description || item.product?.name || item.product?.code || "-"}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary">
              Productos: No hay productos
            </Typography>
          )}
          <Typography color="textSecondary">
            Solicitado por: {outbound.requestedBy}
          </Typography>
          <Typography color="textSecondary">
            Fecha: {new Date(outbound.createdAt).toLocaleDateString()}
          </Typography>
        </CardContent>
        <CardActions>
          <Button 
            size="small" 
            color="primary" 
            onClick={() => handleOutboundAction(outbound.id, 'approve')}
          >
            Aprobar
          </Button>
          <Button 
            size="small" 
            color="error" 
            onClick={() => handleOutboundAction(outbound.id, 'reject')}
          >
            Rechazar
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Box py={3}>
          <Typography variant="h4" gutterBottom>
            Aprobaciones de Inventario
          </Typography>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Entradas" />
              <Tab label="Salidas" />
            </Tabs>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TabPanel value={tabValue} index={0}>
                {inbounds.length === 0 ? (
                  <Typography variant="h6" color="text.secondary" align="center">
                    No hay entradas pendientes de aprobación
                  </Typography>
                ) : (
                  <Grid container spacing={3}>
                    {inbounds.map(renderInboundCard)}
                  </Grid>
                )}
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                {outbounds.length === 0 ? (
                  <Typography variant="h6" color="text.secondary" align="center">
                    No hay salidas pendientes de aprobación
                  </Typography>
                ) : (
                  <Grid container spacing={3}>
                    {outbounds.map(renderOutboundCard)}
                  </Grid>
                )}
              </TabPanel>
            </>
          )}
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default InventoryApprovalScreen; 